import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentAdmin } from "@/hooks/useCurrentAdmin";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useMobile } from "@/hooks/useMobile";
import {
  LayoutDashboard,
  FileText,
  GitBranch,
  Mail,
  Video,
  MessageSquare,
  Settings,
  Users,
  UserCog,
  ClipboardList,
  Ticket,
  FileSearch,
  LogOut,
  Home,
  ChevronDown,
  Menu,
  ShieldCheck,
  Download,
  HardDrive,
  Truck,
  Upload,
  AlertTriangle,
  Sparkles,
  ThumbsUp,
  BookOpen,
  Mic,
  Eye,
  CalendarDays,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useState } from "react";

interface NavSection {
  label: string;
  roles: string[];
  items: NavItem[];
}

interface NavItem {
  icon: any;
  label: string;
  path: string;
  roles?: string[];
}

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const { admin, loading, isSuperAdmin, isContentAdmin, isSupportAdmin } = useCurrentAdmin();
  const { isMobile } = useMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    content: true,
    users: false,
    support: false,
    stewContent: false,
    system: false,
  });

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const canAccessSection = (roles: string[]): boolean => {
    if (roles.includes('all')) return true;
    if (roles.includes('super_admin') && isSuperAdmin()) return true;
    if (roles.includes('content_admin') && isContentAdmin()) return true;
    if (roles.includes('support_admin') && isSupportAdmin()) return true;
    return false;
  };

  const canAccessItem = (item: NavItem): boolean => {
    if (!item.roles || item.roles.length === 0) return true;
    return canAccessSection(item.roles);
  };

  const navigationSections: Record<string, NavSection> = {
    content: {
      label: 'Content Management',
      roles: ['super_admin', 'content_admin'],
      items: [
        { icon: FileText, label: 'Document Templates', path: '/admin/templates/documents' },
        { icon: GitBranch, label: 'Workflow Templates', path: '/admin/templates/workflows' },
        // Removed: Email Templates and Training Content nav items pointed to
        // routes that do not exist (/admin/templates/emails, /admin/training).
        // They caused 404s in the Error Register and will return once those
        // pages ship.
      ],
    },
    users: {
      label: 'User Management',
      roles: ['super_admin', 'support_admin'],
      items: [
        { icon: Users, label: 'All Users', path: '/admin/users' },
        { icon: ClipboardList, label: 'Task Management', path: '/admin/users/tasks' },
        { icon: UserCog, label: 'Admin Accounts', path: '/admin/users/admins', roles: ['super_admin'] },
      ],
    },
    support: {
      label: 'Support',
      roles: ['all'],
      items: [
        { icon: Sparkles, label: 'Stew for Agents', path: '/admin/stew' },
        { icon: Ticket, label: 'Support Tickets', path: '/admin/support/tickets' },
        { icon: MessageSquare, label: 'Member Feedback', path: '/admin/support/feedback' },
      ],
    },
    stewContent: {
      label: 'Stew and Content',
      roles: ['all'],
      items: [
        { icon: FileText, label: 'Briefing Corpus', path: '/admin/briefing-corpus' },
        { icon: ThumbsUp, label: 'Stew Improvement', path: '/admin/stew-improvement' },
        { icon: Sparkles, label: 'Prospect Conversations', path: '/admin/prospect-conversations' },
        { icon: AlertTriangle, label: 'Stew Delivery Failures', path: '/admin/system/stew-mirror' },
        { icon: BookOpen, label: 'Library Editor', path: '/admin/library' },
        { icon: Mic, label: 'Call Mining', path: '/admin/call-mining' },
        { icon: MessageSquare, label: 'Member Questions', path: '/admin/gathering-questions' },
        { icon: MessageSquare, label: 'Name Review', path: '/admin/names' },
        { icon: HardDrive, label: 'Suite Numbers', path: '/admin/suites' },
        { icon: ClipboardList, label: 'Onboarding', path: '/admin/onboarding' },
        { icon: Truck, label: 'Fulfillment', path: '/admin/fulfillment' },
        { icon: FileSearch, label: 'Formation Review', path: '/admin/review' },
        { icon: CalendarDays, label: 'EIN Calendar', path: '/admin/ein-calendar' },
        { icon: Upload, label: 'Legacy Import', path: '/admin/import-legacy' },
      ],
    },
    system: {
      label: 'System',
      roles: ['super_admin'],
      items: [
        { icon: BookOpen, label: 'Canon Register', path: '/admin/canon' },
        { icon: FileText, label: 'Canon Export', path: '/admin/canon-export' },
        { icon: Sparkles, label: "Stew's Brain", path: '/admin/stew-brain' },
        { icon: FileSearch, label: 'Audit Logs', path: '/admin/system/audit' },
        { icon: Mail, label: 'Content', path: '/admin/content' },


        { icon: Download, label: 'Data Export', path: '/admin/system/export' },
        { icon: AlertTriangle, label: 'Error Register', path: '/admin/system/errors' },
        { icon: HardDrive, label: 'Storage Cleanup', path: '/admin/system/storage' },
        { icon: FileText, label: 'Document Jobs', path: '/admin/docgen' },
      ],
    },
    governance: {
      label: 'Governance',
      roles: ['all'],
      items: [
        { icon: ShieldCheck, label: 'Governance Health', path: '/admin/system/governance-health' },
      ],
    },

  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Sidebar navigation content (shared between desktop and mobile)
  const SidebarContent = () => (
    <>
      <nav className="p-4 space-y-2">
        {/* Dashboard */}
        <Button
          variant={isActive('/admin/dashboard') ? "secondary" : "ghost"}
          onClick={() => handleNavigate('/admin/dashboard')}
          className="w-full justify-start gap-2"
        >
          <LayoutDashboard className="h-4 w-4" />
          Dashboard
        </Button>

        {/* Content Management */}
        {canAccessSection(navigationSections.content.roles) && (
          <Collapsible open={openSections.content} onOpenChange={() => toggleSection('content')}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Content Management
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.content ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-6 space-y-1 mt-1">
              {navigationSections.content.items.filter(canAccessItem).map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.path}
                    variant={isActive(item.path) ? "secondary" : "ghost"}
                    onClick={() => handleNavigate(item.path)}
                    className="w-full justify-start gap-2 text-sm"
                    size="sm"
                  >
                    <Icon className="h-3 w-3" />
                    {item.label}
                  </Button>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* User Management */}
        {canAccessSection(navigationSections.users.roles) && (
          <Collapsible open={openSections.users} onOpenChange={() => toggleSection('users')}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  User Management
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.users ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-6 space-y-1 mt-1">
              {navigationSections.users.items.filter(canAccessItem).map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.path}
                    variant={isActive(item.path) ? "secondary" : "ghost"}
                    onClick={() => handleNavigate(item.path)}
                    className="w-full justify-start gap-2 text-sm"
                    size="sm"
                  >
                    <Icon className="h-3 w-3" />
                    {item.label}
                  </Button>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Support */}
        <Collapsible open={openSections.support} onOpenChange={() => toggleSection('support')}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <Ticket className="h-4 w-4" />
                Support
              </span>
              <ChevronDown className={`h-4 w-4 transition-transform ${openSections.support ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pl-6 space-y-1 mt-1">
            {navigationSections.support.items.filter(canAccessItem).map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.path}
                  variant={isActive(item.path) ? "secondary" : "ghost"}
                  onClick={() => handleNavigate(item.path)}
                  className="w-full justify-start gap-2 text-sm"
                  size="sm"
                >
                  <Icon className="h-3 w-3" />
                  {item.label}
                </Button>
              );
            })}
          </CollapsibleContent>
        </Collapsible>

        {/* Stew and Content */}
        {canAccessSection(navigationSections.stewContent.roles) && (
          <Collapsible open={openSections.stewContent} onOpenChange={() => toggleSection('stewContent')}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Stew and Content
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.stewContent ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-6 space-y-1 mt-1">
              {navigationSections.stewContent.items.filter(canAccessItem).map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.path}
                    variant={isActive(item.path) ? "secondary" : "ghost"}
                    onClick={() => handleNavigate(item.path)}
                    className="w-full justify-start gap-2 text-sm"
                    size="sm"
                  >
                    <Icon className="h-3 w-3" />
                    {item.label}
                  </Button>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        )}


        {/* System */}
        {canAccessSection(navigationSections.system.roles) && (
          <Collapsible open={openSections.system} onOpenChange={() => toggleSection('system')}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  System
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${openSections.system ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pl-6 space-y-1 mt-1">
              {navigationSections.system.items.filter(canAccessItem).map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.path}
                    variant={isActive(item.path) ? "secondary" : "ghost"}
                    onClick={() => handleNavigate(item.path)}
                    className="w-full justify-start gap-2 text-sm"
                    size="sm"
                  >
                    <Icon className="h-3 w-3" />
                    {item.label}
                  </Button>
                );
              })}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Governance — visible to any active admin */}
        {canAccessSection(navigationSections.governance.roles) && (
          <div className="space-y-1">
            {navigationSections.governance.items.filter(canAccessItem).map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.path}
                  variant={isActive(item.path) ? "secondary" : "ghost"}
                  onClick={() => handleNavigate(item.path)}
                  className="w-full justify-start gap-2"
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Button>
              );
            })}
          </div>
        )}
      </nav>

      <div className="p-4 border-t bg-card mt-auto">
        <Button
          variant="ghost"
          onClick={() => handleNavigate('/dashboard')}
          className="w-full justify-start gap-2 mb-2"
        >
          <Home className="h-4 w-4" />
          User Portal
        </Button>
        <Button
          variant="ghost"
          onClick={() => signOut()}
          className="w-full justify-start gap-2"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </Button>
        <div className="mt-4 text-xs text-muted-foreground text-center">
          Version 1.0.0
        </div>
      </div>
    </>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="container mx-auto px-6 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </header>
        <div className="flex">
          <aside className="w-64 border-r min-h-[calc(100vh-73px)] bg-card p-4 hidden md:block">
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          </aside>
          <main className="flex-1 p-6">
            <Skeleton className="h-96 w-full" />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Mobile Header */}
      {isMobile && (
        <header className="sticky top-0 z-50 border-b bg-card px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">Admin Portal</h1>
            <p className="text-xs text-muted-foreground">
              {admin?.admin_level.replace('_', ' ')}
            </p>
          </div>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px]">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
              <div className="p-4 border-b">
                <h2 className="text-lg font-bold">Admin Portal</h2>
                <p className="text-sm text-muted-foreground">
                  {admin?.admin_level.replace('_', ' ')}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto">
                <SidebarContent />
              </div>
            </SheetContent>
          </Sheet>
        </header>
      )}

      {/* Desktop Left Sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 border-r min-h-screen bg-card">
        <div className="p-6 border-b">
          <h1 className="text-xl font-bold">Admin Portal</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {admin?.admin_level.replace('_', ' ')}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto">
          <SidebarContent />
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
